import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { LANG, LOCALE, t } from "../../lib/i18n";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, ArrowUp, ArrowUpRight, Check, X } from "lucide-react";
import { API, post } from "../../lib/api";
import { onScrollFrame } from "../motion/Kinetic";

/* ------------------------------------------------------------------ constants */

const SESSION_KEY = "eqva.ask.session";
const TRANSCRIPT_KEY = "eqva.ask.transcript";
const QUICK_LINKS = [[t("Investor"), "/investor"], [t("Selskapene"), "/selskaper"], [t("Karriere"), "/om-oss/karriere"], [t("Kontakt"), "/om-oss/kontakt"]];

const SUGGESTIONS = [
  { label: t("Vi har et prosjekt som trenger flere fag"), text: t("Vi har et prosjekt som trenger flere fag. Hvem i EQVA bør vi snakke med?") },
  { label: t("Jeg vurderer neste steg for selskapet mitt"), text: t("Jeg eier et industriselskap og vurderer neste steg. Hvordan tenker EQVA som eier?") },
  { label: t("Jeg ser etter jobb i et av selskapene"), text: t("Jeg ser etter jobb i et av EQVA-selskapene.") },
  { label: t("Siste resultater og neste rapport"), text: t("Hva var de siste resultatene, og når kommer neste rapport?") },
];

/* Where the launcher gets a contextual label */
const ROUTE_LABELS = [
  [/^\/bli-en-del-av-eqva/, t("Spør om eierskap")],
  [/^\/om-oss\/karriere/, t("Spør om jobb")],
  [/^\/investor/, t("Spør om aksjen")],
];

const KIND_MAP = { project: "general", investor: "general", contact: "general", anonymous: "acquisition", acquisition: "acquisition", career: "career", general: "general" };

/** Open «Spør EQVA» from anywhere on the page with a prompt (used by the on-page entrance). */
export const askEqva = (text) => window.dispatchEvent(new CustomEvent("eqva:ask", { detail: { text: String(text || "") } }));

const uid = () => (crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2) + Date.now().toString(36));

const load = (k, fallback) => { try { const v = sessionStorage.getItem(k); return v ? JSON.parse(v) : fallback; } catch { return fallback; } };
const save = (k, v) => { try { sessionStorage.setItem(k, JSON.stringify(v)); } catch { /* private mode */ } };

const fmtDate = (iso) => { if (!iso) return ""; const d = new Date(iso + (iso.length === 10 ? "T12:00:00" : "")); return isNaN(d) ? iso : d.toLocaleDateString(LOCALE, { day: "numeric", month: "short", year: "numeric" }); };

/* ------------------------------------------------------------------ tiny markdown (bold, links, lists, paragraphs) */

const inline = (text, keyBase) => {
  const parts = [];
  const re = /\*\*(.+?)\*\*|\[([^\]]+)\]\(([^)\s]+)\)/g;
  let last = 0, m, i = 0;
  while ((m = re.exec(text))) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    if (m[1] !== undefined) parts.push(<strong key={`${keyBase}-b${i++}`} className="font-semibold text-white">{m[1]}</strong>);
    else {
      const href = m[3];
      const cls = "underline decoration-white/40 underline-offset-[3px] hover:decoration-white transition-colors";
      parts.push(href.startsWith("/")
        ? <Link key={`${keyBase}-l${i++}`} to={href} className={cls}>{m[2]}</Link>
        : <a key={`${keyBase}-l${i++}`} href={href} target="_blank" rel="noreferrer" className={cls}>{m[2]}</a>);
    }
    last = re.lastIndex;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
};

export const Prose = ({ text = "", className = "" }) => {
  const blocks = useMemo(() => {
    const out = [];
    const lines = text.replace(/\r/g, "").split("\n");
    let para = [], list = [];
    const flushP = () => { if (para.length) { out.push({ t: "p", v: para.join(" ") }); para = []; } };
    const flushL = () => { if (list.length) { out.push({ t: "ul", v: list }); list = []; } };
    for (const raw of lines) {
      const l = raw.trim();
      if (!l) { flushP(); flushL(); continue; }
      if (/^[-•*]\s+/.test(l)) { flushP(); list.push(l.replace(/^[-•*]\s+/, "")); continue; }
      flushL(); para.push(l);
    }
    flushP(); flushL();
    return out;
  }, [text]);
  return (
    <div className={`ask-prose ${className}`}>
      {blocks.map((b, i) => b.t === "p"
        ? <p key={i}>{inline(b.v, i)}</p>
        : <ul key={i}>{b.v.map((li, j) => <li key={j}>{inline(li, `${i}-${j}`)}</li>)}</ul>)}
    </div>
  );
};

/* ------------------------------------------------------------------ transport
   Streaming first. Production proxies sometimes buffer or cut text/event-stream responses; the server now sends
   its first bytes at once, so if nothing arrives within FIRST_BYTE_MS the stream is not getting through and we
   fall back to the plain JSON endpoint (and remember that for the rest of the session). A stalled stream –
   bytes stopped mid-answer – ends in an honest error instead of an endless caret. */

const FIRST_BYTE_MS = 6_000;
const STALL_MS = 45_000;
const COMPLETE_MS = 120_000;
const MODE_KEY = "eqva-ask-transport";

const tagged = (code, message) => Object.assign(new Error(message), { code });

async function streamAsk(body, handlers, ctrl) {
  let timer = 0, gotBytes = false, why = null;
  const arm = (ms, code) => { clearTimeout(timer); timer = setTimeout(() => { why = code; ctrl.abort(); }, ms); };
  arm(FIRST_BYTE_MS, "no-stream");
  let res;
  try {
    res = await fetch(`${API}/ask`, { method: "POST", headers: { "Content-Type": "application/json", Accept: "text/event-stream" }, body: JSON.stringify(body), signal: ctrl.signal });
  } catch (e) {
    clearTimeout(timer);
    if (ctrl.signal.aborted) throw tagged(why || "aborted", t("Ingen kontakt med tjenesten."));
    throw tagged("network", t("Ingen kontakt med tjenesten."));
  }
  if (!res.ok) {
    clearTimeout(timer);
    let detail = "Noe gikk galt.";
    try { detail = (await res.json()).detail || detail; } catch { /* ignore */ }
    throw tagged(res.status === 429 ? "rate" : "http", t(detail));
  }
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = "";
  try {
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      gotBytes = true;
      arm(STALL_MS, "stalled");
      buf += dec.decode(value, { stream: true });
      let idx;
      while ((idx = buf.indexOf("\n\n")) !== -1) {
        const frame = buf.slice(0, idx); buf = buf.slice(idx + 2);
        const line = frame.split("\n").find((l) => l.startsWith("data:"));
        if (!line) continue; // comment frames (": open", ": ping") keep the connection warm – nothing to parse
        try { handlers(JSON.parse(line.slice(5))); } catch { /* malformed frame – skip */ }
      }
    }
  } catch (e) {
    if (ctrl.signal.aborted) throw tagged(why || "aborted", gotBytes ? t("Forbindelsen ble brutt underveis.") : t("Ingen kontakt med tjenesten."));
    throw tagged(gotBytes ? "broken" : "network", t("Forbindelsen ble brutt underveis."));
  } finally {
    clearTimeout(timer);
  }
  if (!gotBytes) throw tagged("no-stream", t("Ingen kontakt med tjenesten."));
}

async function completeAsk(body, handlers, ctrl) {
  let why = null;
  const timer = setTimeout(() => { why = "slow"; ctrl.abort(); }, COMPLETE_MS);
  try {
    const res = await fetch(`${API}/ask/complete`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body), signal: ctrl.signal });
    if (!res.ok) {
      let detail = "Noe gikk galt.";
      try { detail = (await res.json()).detail || detail; } catch { /* ignore */ }
      throw tagged(res.status === 429 ? "rate" : "http", t(detail));
    }
    const d = await res.json();
    handlers({ type: "meta", session_id: d.session_id, conversation_id: d.conversation_id });
    handlers({ type: "done", text: d.text, sources: d.sources || [], actions: d.actions || [], inquiry: d.inquiry || null });
  } catch (e) {
    if (e.code) throw e;
    if (ctrl.signal.aborted) throw tagged(why || "aborted", t("Svaret tok for lang tid. Prøv igjen om et øyeblikk."));
    throw tagged("network", t("Ingen kontakt med tjenesten. Prøv igjen om et øyeblikk."));
  } finally {
    clearTimeout(timer);
  }
}

/** Streaming when it works, JSON when it does not – decided once per session, re-decided on failure. */
async function askTransport(body, handlers, ctrl) {
  let received = false;
  const seen = (ev) => { if (ev.type === "delta" || ev.type === "done") received = true; handlers(ev); };
  if (sessionStorage.getItem(MODE_KEY) === "json") return completeAsk(body, handlers, ctrl);
  try {
    await streamAsk(body, seen, ctrl);
  } catch (e) {
    const userCancelled = e.code === "aborted" || e.code === "user";
    if (userCancelled || received || e.code === "rate" || e.code === "http") throw e;
    // Nothing useful came through the stream – switch transport and ask once more.
    sessionStorage.setItem(MODE_KEY, "json");
    const retry = new AbortController();
    ctrl.signal.addEventListener("abort", () => retry.abort(), { once: true });
    return completeAsk(body, handlers, retry);
  }
}

/* ------------------------------------------------------------------ intake card */

const InquiryCard = ({ draft, sessionId, conversationId, onSent, onDismiss }) => {
  const [f, setF] = useState({ name: draft.name || "", email: draft.email || "", phone: draft.phone || "", company: draft.company || "", message: draft.message || "" });
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const kind = KIND_MAP[draft.kind] || "general";
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    if (!consent || busy) return;
    setBusy(true); setErr("");
    try {
      const r = await post("/inquiries", { kind, name: f.name, email: f.email, phone: f.phone || null, company: f.company || null, message: f.message, consent: true,
        meta: { source: "spor-eqva", session_id: sessionId, conversation_id: conversationId || "" } });
      onSent(r.reference);
    } catch (ex) {
      setErr(ex?.response?.data?.detail ? t(ex.response.data.detail) : t("Kunne ikke sende. Sjekk e-postadressen og prøv igjen."));
    } finally { setBusy(false); }
  };
  const field = "w-full bg-white/[0.06] border border-white/15 px-3.5 py-2.5 text-[15px] text-white placeholder:text-white/40 outline-none focus-visible:border-white/60 transition-colors";
  return (
    <form onSubmit={submit} data-testid="ask-inquiry-card" className="mt-5 border border-white/20 p-5 md:p-6">
      <p className="t-label text-white/60">{t("Henvendelse til EQVA")}</p>
      <p className="mt-2 text-[17px] font-medium leading-snug">{t("Se over – så går dette til riktig person i EQVA.")}</p>
      <div className="mt-5 grid gap-3 sm:grid-cols-2">
        <label className="block"><span className="sr-only">{t("Navn")}</span><input data-testid="ask-inquiry-name" className={field} value={f.name} onChange={set("name")} placeholder={t("Navn")} required minLength={2} /></label>
        <label className="block"><span className="sr-only">{t("E-post")}</span><input data-testid="ask-inquiry-email" className={field} type="email" value={f.email} onChange={set("email")} placeholder={t("E-post")} required /></label>
        <label className="block"><span className="sr-only">{t("Telefon")}</span><input data-testid="ask-inquiry-phone" className={field} value={f.phone} onChange={set("phone")} placeholder={t("Telefon (valgfritt)")} /></label>
        <label className="block"><span className="sr-only">{t("Selskap")}</span><input data-testid="ask-inquiry-company" className={field} value={f.company} onChange={set("company")} placeholder={t("Selskap (valgfritt)")} /></label>
      </div>
      <label className="mt-3 block"><span className="sr-only">{t("Oppsummering")}</span><textarea data-testid="ask-inquiry-message" className={`${field} min-h-[112px] resize-y leading-relaxed`} value={f.message} onChange={set("message")} required minLength={10} /></label>
      <label className="mt-4 flex items-start gap-3 text-[14px] text-white/75 cursor-pointer">
        <input data-testid="ask-inquiry-consent" type="checkbox" className="mt-[3px] h-4 w-4 accent-white" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
        <span>{t("Jeg samtykker til at EQVA behandler opplysningene for å besvare henvendelsen.")}{" "}<Link to="/personvern" className="underline underline-offset-2">{t("Personvern")}</Link></span>
      </label>
      {err && <p data-testid="ask-inquiry-error" className="mt-3 text-[14px] text-[#F3B6A0]">{err}</p>}
      <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-3">
        <button type="submit" data-testid="ask-inquiry-send" disabled={!consent || busy} className="btn-pill bg-white text-navy hover:bg-white/90 disabled:opacity-40 disabled:cursor-not-allowed">
          {busy ? t("Sender …") : t("Send henvendelsen")} <ArrowRight size={16} />
        </button>
        <button type="button" data-testid="ask-inquiry-dismiss" onClick={onDismiss} className="text-[14px] text-white/60 hover:text-white transition-colors">{t("Ikke nå")}</button>
      </div>
    </form>
  );
};

/* ------------------------------------------------------------------ the assistant */

export const AskEqva = () => {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [shown, setShown] = useState(false);
  const [inverted, setInverted] = useState(false);
  const [ctxLabel, setCtxLabel] = useState(null);
  const [sessionId, setSessionId] = useState(() => load(SESSION_KEY, null));
  const [conversationId, setConversationId] = useState(null);
  const [messages, setMessages] = useState(() => load(TRANSCRIPT_KEY, []));
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(null);   // inquiry draft awaiting confirmation
  const [sentRef, setSentRef] = useState(null);
  const [suppressed, setSuppressed] = useState(false);
  const queued = useRef(null);
  const launcherRef = useRef(null);
  const listRef = useRef(null);
  const inputRef = useRef(null);
  const abortRef = useRef(null);
  const [slow, setSlow] = useState(false);
  /* The launcher stays out of the first view: on the front page it appears once the film has (mostly) scrolled past,
     elsewhere after ~400px – and retires again at the very top. */
  useEffect(() => onScrollFrame(() => {
    const media = document.querySelector("[data-testid=hero-media]");
    if (media) {
      const r = media.getBoundingClientRect();
      setShown(r.bottom < window.innerHeight * 0.25);
    } else {
      setShown(window.scrollY > 400);
    }
  }), [pathname]);

  /* Invert over dark surfaces and pick up a contextual label from the section beneath.
     Re-checked once more ~250ms after the last scroll frame, since the page may still be settling (hero growth). */
  useEffect(() => {
    if (open) return;
    let t;
    const probe = () => {
      const el = launcherRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const under = document.elementsFromPoint(r.left + r.width / 2, r.top + r.height / 2).find((n) => n !== el && !el.contains(n));
      const dark = !!under?.closest?.(".bg-navy, .bg-ink, [data-tone='dark']");
      setInverted(dark);
      setSuppressed(!!under?.closest?.("[data-ask-suppress]"));
      const route = ROUTE_LABELS.find(([re]) => re.test(pathname));
      const section = under?.closest?.("[data-ask-label]");
      setCtxLabel(section?.getAttribute("data-ask-label") || (route ? route[1] : null));
    };
    const off = onScrollFrame(() => { probe(); clearTimeout(t); t = setTimeout(probe, 250); });
    return () => { off(); clearTimeout(t); };
  }, [open, pathname]);

  /* Persist – and restore a conversation from the server if this tab only kept the session id */
  useEffect(() => { if (sessionId) save(SESSION_KEY, sessionId); }, [sessionId]);
  useEffect(() => {
    if (!open || !sessionId || messages.length > 0) return;
    fetch(`${API}/ask/${sessionId}`).then((r) => (r.ok ? r.json() : null)).then((d) => {
      if (!d?.messages?.length) return;
      setMessages(d.messages.map((m) => ({ id: uid(), role: m.role, content: m.content, sources: m.sources || [], actions: [], hidden: m.role === "user" && /^Henvendelsen er sendt/.test(m.content) })));
    }).catch(() => {});
  }, [open, sessionId, messages.length]);

  /* Composer grows with its content (one to five lines) */
  const grow = (el) => { if (!el) return; el.style.height = "auto"; el.style.height = `${Math.min(el.scrollHeight, 150)}px`; };
  useEffect(() => { save(TRANSCRIPT_KEY, messages.slice(-40)); }, [messages]);

  /* Drawer: lock scroll, Escape closes, focus the composer */
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    const t = setTimeout(() => inputRef.current?.focus(), 350);
    return () => { document.body.style.overflow = prev; window.removeEventListener("keydown", onKey); clearTimeout(t); };
  }, [open]);

  /* Keep the newest message in view while streaming */
  useLayoutEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, pending, sentRef, busy]);

  const send = useCallback(async (text, { hidden = false } = {}) => {
    const clean = (text || "").trim();
    if (!clean || busy) return;
    setBusy(true); setInput(""); setPending(null);
    if (inputRef.current) inputRef.current.style.height = "auto";
    const userMsg = { id: uid(), role: "user", content: clean, hidden };
    const botMsg = { id: uid(), role: "assistant", content: "", sources: [], actions: [], streaming: true };
    setMessages((m) => [...m, userMsg, botMsg]);
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    const slowT = setTimeout(() => setSlow(true), 5000);
    const settle = () => { clearTimeout(slowT); setSlow(false); };
    try {
      await askTransport({ message: clean, session_id: sessionId, path: pathname, lang: LANG }, (ev) => {
        if (ev.type === "meta") { if (ev.session_id && ev.session_id !== sessionId) setSessionId(ev.session_id); if (ev.conversation_id) setConversationId(ev.conversation_id); }
        else if (ev.type === "delta") { settle(); setMessages((m) => m.map((x) => (x.id === botMsg.id ? { ...x, content: x.content + ev.text } : x))); }
        else if (ev.type === "done") {
          settle();
          setMessages((m) => m.map((x) => (x.id === botMsg.id ? { ...x, content: ev.text || x.content, sources: ev.sources || [], actions: ev.actions || [], streaming: false } : x)));
          if (ev.inquiry) setPending(ev.inquiry);
        } else if (ev.type === "error") {
          settle();
          setMessages((m) => m.map((x) => (x.id === botMsg.id ? { ...x, content: t(ev.message), streaming: false, error: true, retry: hidden ? null : clean } : x)));
        }
      }, ctrl);
    } catch (e) {
      if (e.code !== "user") setMessages((m) => m.map((x) => (x.id === botMsg.id ? { ...x, content: e.message || t("Noe gikk galt. Prøv igjen."), streaming: false, error: true, retry: hidden ? null : clean } : x)));
    } finally {
      settle();
      setMessages((m) => m.map((x) => (x.id === botMsg.id && x.streaming ? { ...x, streaming: false } : x)));
      setBusy(false);
    }
  }, [busy, sessionId, pathname]);

  useEffect(() => {
    const onAsk = (e) => { queued.current = e.detail?.text || ""; setOpen(true); };
    window.addEventListener("eqva:ask", onAsk);
    return () => window.removeEventListener("eqva:ask", onAsk);
  }, []);
  useEffect(() => {
    if (!open || !queued.current) return;
    const text = queued.current; queued.current = null;
    const t = setTimeout(() => send(text), 420);
    return () => clearTimeout(t);
  }, [open, send]);

  const onSubmit = (e) => { e.preventDefault(); send(input); };
  const onKeyDown = (e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(input); } };

  /* A failed turn can be asked again: drop the failed pair and resend the same text */
  const retry = (m) => {
    if (busy || !m.retry) return;
    setMessages((prev) => { const i = prev.findIndex((x) => x.id === m.id); return i > 0 ? prev.slice(0, i - 1) : prev; });
    setTimeout(() => send(m.retry), 0);
  };

  const onSent = (ref) => {
    setSentRef(ref); setPending(null);
    send(t("Henvendelsen er sendt og registrert med referanse {ref}.", { ref }), { hidden: true });
  };

  /* Start over: drop the transcript and the session so the next question opens a fresh conversation */
  const resetConversation = () => {
    abortRef.current?.abort?.();
    setMessages([]); setPending(null); setSentRef(null); setConversationId(null); setSessionId(null); setBusy(false);
    try { sessionStorage.removeItem(SESSION_KEY); sessionStorage.removeItem(TRANSCRIPT_KEY); } catch { /* private mode */ }
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const label = ctxLabel || t("Spør EQVA");
  const visible = messages.filter((m) => !m.hidden);
  const lastBot = [...messages].reverse().find((m) => m.role === "assistant" && !m.streaming);

  return (
    <>
      {/* Launcher – a typographic control, not a bubble */}
      <button
        ref={launcherRef}
        type="button"
        data-testid="ask-launcher"
        aria-label={t("Spør EQVA")}
        aria-expanded={open}
        onClick={() => setOpen(true)}
        className={`ask-launcher ${shown && !open && !suppressed ? "is-shown" : ""} ${inverted ? "is-inverted" : ""}`}
      >
        <span className="hidden sm:inline">{label}</span>
        <span className="sm:hidden">{t("Spør")}</span>
        <ArrowRight size={14} strokeWidth={1.75} aria-hidden="true" />
      </button>

      {/* Drawer */}
      <div className={`ask-backdrop ${open ? "is-open" : ""}`} onClick={() => setOpen(false)} aria-hidden="true" />
      <aside data-testid="ask-drawer" role="dialog" aria-modal="true" aria-label={t("Spør EQVA")} aria-hidden={!open} className={`ask-drawer ${open ? "is-open" : ""}`}>
        <div className="flex items-center justify-between px-6 md:px-10 pt-6 md:pt-7">
          <p className="t-label text-white/70">{t("Spør EQVA")}</p>
          <div className="flex items-center gap-1">
            {visible.length > 0 && <button type="button" data-testid="ask-reset" onClick={resetConversation} className="px-2 py-2 t-small text-white/60 transition-colors hover:text-white">{t("Ny samtale")}</button>}
            <button type="button" data-testid="ask-close" aria-label={t("Lukk")} onClick={() => setOpen(false)} className="-mr-2 p-2 text-white/70 hover:text-white transition-colors"><X size={22} /></button>
          </div>
        </div>

        <div ref={listRef} className="ask-scroll flex-1 overflow-y-auto px-6 md:px-10 pb-6">
          {visible.length === 0 ? (
            <div className="pt-10 md:pt-16">
              <h2 className="text-[clamp(1.75rem,2.4vw,2.375rem)] font-medium leading-[1.1] tracking-[-0.025em] max-w-[16ch] text-balance">{t("Hva gjelder det?")}</h2>
              <p className="mt-4 t-body text-white/70 max-w-[42ch]">{t("Et prosjekt, et selskap, en jobb eller aksjen. Beskriv det som til en kollega – så finner vi riktig selskap og riktig person i EQVA.")}</p>
              <ul className="mt-9 border-t border-white/15">
                {SUGGESTIONS.map((s, i) => (
                  <li key={s.label} className="border-b border-white/15">
                    <button type="button" data-testid={`ask-suggestion-${i}`} onClick={() => send(s.text)} className="group flex w-full items-center justify-between gap-6 py-4 text-left text-[17px] font-medium transition-colors hover:text-white/80">
                      <span>{s.label}</span>
                      <ArrowUpRight size={18} className="shrink-0 text-white/50 transition-[transform,color] duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-white" />
                    </button>
                  </li>
                ))}
              </ul>
              <p className="mt-7 t-small text-white/50">
                {t("Eller gå rett til")}{" "}
                {QUICK_LINKS.map(([l, href], i) => <span key={href}>{i > 0 && <span aria-hidden="true"> · </span>}<Link to={href} data-testid={`ask-quick-${href.replace(/\W+/g, "-").replace(/^-|-$/g, "")}`} onClick={() => setOpen(false)} className="text-white/80 underline decoration-white/30 underline-offset-[3px] transition-colors hover:text-white hover:decoration-white">{l}</Link></span>)}
              </p>
            </div>
          ) : (
            <ol className="pt-8 space-y-7">
              {visible.map((m, i) => (
                <li key={m.id} data-testid={`ask-message-${i}`} data-role={m.role} className={m.role === "user" ? "flex justify-end" : ""}>
                  {m.role === "user" ? (
                    <p className="max-w-[85%] bg-white/[0.08] px-4 py-3 text-[15px] leading-relaxed text-white whitespace-pre-wrap">{m.content}</p>
                  ) : (
                    <div className="max-w-[92%]">
                      {m.content ? <Prose text={m.content} className={m.error ? "text-[#F3B6A0]" : ""} /> : <span className="ask-caret" aria-label={t("Skriver …")} />}
                      {m.streaming && m.content && <span className="ask-caret ml-0.5" />}
                      {m.streaming && !m.content && slow && <p data-testid="ask-slow-hint" className="mt-3 text-[13px] text-white/50">{t("Et øyeblikk – svaret er på vei.")}</p>}
                      {m.error && m.retry && m === lastBot && !busy && (
                        <button type="button" data-testid="ask-retry" onClick={() => retry(m)} className="mt-3 inline-flex items-center gap-2 border border-white/30 px-3.5 py-2 text-[14px] font-medium transition-colors hover:border-white hover:bg-white/[0.06]">
                          {t("Prøv igjen")} <ArrowRight size={14} />
                        </button>
                      )}
                      {m.sources?.length > 0 && (
                        <ul data-testid="ask-sources" className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5 text-[13px] text-white/60">
                          {m.sources.map((s, j) => (
                            <li key={j}>
                              {t("Kilde")}: {s.href?.startsWith("/") ? <Link to={s.href} className="underline underline-offset-2 hover:text-white">{s.title}</Link> : <span>{s.title}</span>}
                              {s.date && <span> · {fmtDate(s.date)}</span>}
                            </li>
                          ))}
                        </ul>
                      )}
                      {m === lastBot && !pending && !busy && m.actions?.length > 0 && (
                        <div className="mt-4 flex flex-wrap gap-2">
                          {m.actions.map((a, j) => (
                            <button key={j} type="button" data-testid={`ask-action-${j}`} onClick={() => send(a.label)} className="inline-flex items-center gap-2 border border-white/30 px-3.5 py-2 text-[14px] font-medium transition-colors hover:border-white hover:bg-white/[0.06]">
                              {a.label} <ArrowRight size={14} />
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </li>
              ))}
              {pending && <li><InquiryCard draft={pending} sessionId={sessionId} conversationId={conversationId} onSent={onSent} onDismiss={() => setPending(null)} /></li>}
              {sentRef && (
                <li data-testid="ask-inquiry-ref" className="flex items-center gap-3 border border-white/20 px-4 py-3 text-[14px]">
                  <Check size={16} className="shrink-0 text-white" />
                  <span>{t("Sendt. Referanse")} <span className="tabular font-semibold">{sentRef}</span>. {t("Du hører fra oss.")}</span>
                </li>
              )}
            </ol>
          )}
        </div>

        <form onSubmit={onSubmit} className="ask-composer px-6 md:px-10 py-4 md:py-5">
          <div className="flex items-end gap-3">
            <textarea
              ref={inputRef}
              data-testid="ask-input"
              value={input}
              onChange={(e) => { setInput(e.target.value); grow(e.target); }}
              onKeyDown={onKeyDown}
              rows={1}
              maxLength={2000}
              placeholder={t("Hva gjelder det?")}
              aria-label={t("Skriv spørsmålet ditt")}
              className="ask-input flex-1 resize-none bg-transparent text-[16px] leading-relaxed text-white placeholder:text-white/40 outline-none"
            />
            <button type="submit" data-testid="ask-send" disabled={!input.trim() || busy} aria-label={t("Send")} className="grid h-10 w-10 shrink-0 place-items-center bg-white text-navy transition-opacity disabled:opacity-30">
              <ArrowUp size={18} />
            </button>
          </div>
          <p className="mt-3 text-[12px] leading-relaxed text-white/45">{t("Svarene bygger på det EQVA og selskapene har publisert. Et menneske tar over når du vil.")}</p>
        </form>
      </aside>
    </>
  );
};
